import { useState, useEffect, useCallback } from "react";
import { getAccessToken } from "../../services/authApi";
import { getTPOContact, getUserDetails, getTpoSettingsBasicInfo, getSalesRepOrg, getTPOContactByOrg } from "../../services/userApi";
import { convertMismoToLoanObject, createLoan, updateLienHolderFields, getArmIndex, getMilestoneFreeRoles, patchMilestoneFreeRoles, fieldWriter } from "../../services/loanApi";
import { useDropzone } from "react-dropzone";
import '../../common.css';
import './Loan.css';
import { logError } from "../../utils/logError";
import Loading from "../layout/Loading";
import FormLoader from "../layout/FormLoader";
import { info, error as logAppInsightError } from '../../utils/logger';
import { getCurrentPSTDateAndTimeISO } from '../../utils/formatDate';
import { getPhoenixISODate } from '../../utils/dateUtils';
import Toast from '../shared/Toast';
import { collectSoftLicenseWarnings } from '../../utils/licenseValidation';
import {
    getActiveLicenseForState,
    extractCompanyDetails,
    extractUserDetails,
    buildInterviewerName,
    applyStaticImportParityForManual,
    getProgressWidth,
    formatLoanErrorMessage,
    parseErrorModalBlocks,
    clearRentalIncomeWithoutReo,
    getImportedRentalIncomeFieldWrite
} from "../../utils/loanHelper";
import ErrorBlocks from "../shared/ErrorBlocks";

const LoanForm = () => {
    const [accessToken, setAccessToken] = useState(null);
    const [uploadedXmlContent, setUploadedXmlContent] = useState("");
    const [loanType, setLoanType] = useState(null);
    const [method, setMethod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);
    const [uploadedFile, setUploadedFile] = useState(null);

    const [loanOfficerList, setLoanOfficerList] = useState([]);
    const [loanProcessorList, setLoanProcessorList] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedLoanOfficer, setSelectedLoanOfficer] = useState("");
    const [selectedLoanProcessor, setSelectedLoanProcessor] = useState("");

    const [tpoCompanies, setTpoCompanies] = useState([]);
    const [tpoLoanOfficers, setTpoLoanOfficers] = useState([]);
    const [tpoLoanProcessors, setTpoLoanProcessors] = useState([]);
    const [tpoParent, setTpoParent] = useState(null);
    const [stateLicenses, setStateLicenses] = useState(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [propertyState, setPropertyState] = useState("");
    const [propertyCity, setPropertyCity] = useState("");
    const [fileError, setFileError] = useState("");
    const [tpoUserId, setTpoUserId] = useState(null);
    const [salesRepAeList, setSalesRepAeList] = useState([]);
    const [selectedAeUserId, setSelectedAeUserId] = useState("");

    const [orgOptions, setOrgOptions] = useState([]);
    const [selectedOrgEntityId, setSelectedOrgEntityId] = useState("");
    const [mainOrgCache, setMainOrgCache] = useState(null);
    const [switchingOrg, setSwitchingOrg] = useState(false);
    const [peerLoanAccess, setPeerLoanAccess] = useState("");

    const ENABLE_COMPANY_SWITCH =
        String(process.env.REACT_APP_ENABLE_COMPANY_SWITCH_DROPDOWN || "false").toLowerCase() === "true";

    const isReadWrite = (v) => String(v || "").trim().toLowerCase() === "readwrite";


    const buildUploadContext = (extra = {}) => ({
        component: "LoanForm",
        function: "FileUpload",
        step,
        method,
        loanType,
        hasUploadedFile: !!uploadedFile,
        uploadedFileName: uploadedFile?.name || null,
        uploadedFileSize: uploadedFile?.size || null,
        uploadedFileType: uploadedFile?.type || null,
        hasXmlContent: !!uploadedXmlContent,
        ...extra
    });

    const [forceBusinessFlow, setForceBusinessFlow] = useState(false); // when nmlsId is empty
    const [hideStep1, setHideStep1] = useState(false);
    const [hideBackInStep2, setHideBackInStep2] = useState(false);


    useEffect(() => {
        info("TPO Add New Page loaded", { component: "LoanForm" });

        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
            const errorCode = logError("GUEST_NOT_AVAILABLE");
            setErrorMessage(`Error Code: ${errorCode} - Guest functionality is not available.`);
            setShowErrorPopup(true);

            logAppInsightError("window.elli load failed", {
                problemId: errorCode,
                errorCode,
                errorMessage: `${errorCode} - Guest functionality is not available."}`,
                component: "LoanForm"
            });
        }
    }, []);

    const initializeAuthObject = async () => {

        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");


            try {
                const authCode = await tpoAuthObject.createAuthCode();
                const token = await getAccessToken(authCode);
                if (token) {
                    setAccessToken(token);


                    tpoAuthObject.getUser().then(async function (user) {
                        try {
                            console.log(user.id);

                            // 1) Call getUserDetails on page load (after auth)
                            const userDetails = await getUserDetails(token, user.id);

                            // Get orgId from userDetails.organization.entityId
                            const orgId = userDetails?.organization?.entityId;

                            // 2) Call TPO settings endpoint using orgId
                            const tpoSettings = await getTpoSettingsBasicInfo(token, orgId);

                            // read nmlsId from response
                            const nmlsId =
                                tpoSettings?.basicInfo?.businessInformation?.nmlsId || "";

                            // 3) If nmlsId empty -> change flow as requested
                            if (!String(nmlsId).trim()) {
                                setForceBusinessFlow(true);

                                // hide step 1 and go directly to Business step 2
                                setHideStep1(true);
                                setHideBackInStep2(true);

                                setLoanType("business");
                                setStep(2);
                            }
                            // else: keep current flow
                        } catch (e) {
                            console.error("UserDetails/TPO basicInfo check failed:", e);
                            // keep existing flow (do not block user)
                        }
                        finally {
                            setLoading(false);
                        }
                    });

                }
                else {
                    const errorCode = logError("AUTH_BLOCK_ERROR");
                    logAppInsightError("Authentication failed.", {
                        problemId: errorCode,
                        errorCode,
                        errorMessage: `${errorCode} - Authentication failed."}`,
                        component: "LoanForm"
                    });

                    redirectToErrorPage(`Error Code: ${errorCode} - Authentication failed.`);
                }
            } catch (error) {
                console.error("createAuthCode Error:", error);
                const errorCode = logError("AUTH_BLOCK_ERROR");
                setErrorMessage(`Error Code: ${errorCode} - Authentication failed.`);
                setShowErrorPopup(true);
                logAppInsightError("Authentication failed.", {
                    problemId: errorCode,
                    errorCode,
                    errorMessage: `${errorCode} - Authentication failed."}`,
                    component: "LoanForm"
                });
                redirectToErrorPage(`Error Code: ${errorCode} - Authentication failed.`);
            }
        } catch (error) {
            console.error("Auth block Error:", error);
            const errorCode = logError("AUTH_CODE_ERROR");
            setErrorMessage(`Error Code: ${errorCode} - Unable to generate Auth Code.`);
            setShowErrorPopup(true);
            logAppInsightError("Unable to generate Auth Code.", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Unable to generate Auth Code."}`,
                component: "LoanForm"
            });
            redirectToErrorPage(`Error Code: ${errorCode} - Unable to generate Auth Code.`);
        }
    };

    const redirectToErrorPage = (message) => {
        window.location.href = `/exception?message=${encodeURIComponent(message)}&page=loan`;
    };

    const handleNext = async () => {
        setSubmitting(true);
        let state = "";
        setPropertyState("");
        setPropertyCity("");

        if (method === "import") {

            // 1. NEXT clicked without XML content
            if (!uploadedXmlContent) {
                const errorCode = logError("FILE_UPLOAD_ERROR");

                logAppInsightError("Next clicked without uploaded XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    step,
                    method,
                    loanType,
                    hasUploadedFile: !!uploadedFile
                }));

                setErrorMessage(`Error Code: ${errorCode} - Please upload a MISMO XML file first.`);
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            let loanObject = null;

            try {
                loanObject = await convertMismoToLoanObject(accessToken, uploadedXmlContent);
            } catch (error) {
                const errorCode = logError("MISMO_CONVERT_ERROR");

                logAppInsightError("MISMO convert failed (handleNext)", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    rawError: error?.message || "convertMismoToLoanObject error",
                    responseData: error?.response?.data || null
                }));

                showToast(`❌ ${error.message}`, 8000);
                setSubmitting(false);
                return;
            }

            // 2. Consumer loan uploaded with Investor property
            if (
                loanType === "consumer" &&
                loanObject?.applications?.[0]?.propertyUsageType?.toLowerCase() === "investor"
            ) {
                const errorCode = logError("FILE_UPLOAD_ERROR");

                logAppInsightError("Consumer loan uploaded with investor XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    propertyUsageType: loanObject.applications[0].propertyUsageType
                }));

                setErrorMessage("Please upload a consumer type MISMO XML file");
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            // Extract and store property location
            state = loanObject?.property?.state?.trim() || "";
            const city = loanObject?.property?.city?.trim() || "";
            setPropertyState(state);
            setPropertyCity(city);
        }

        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            fetchExternalUserDetails(authCode, user.id, state);
        });

        setStep(4);
    };

    const handleNextManual = async () => {
        setSubmitting(true);
        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            fetchExternalUserDetails(authCode, user.id, "");
        });

        setStep(4);
    };

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith(".xml")) {
                const errorCode = logError("FILE_TYPE_INVALID"); // or use FILE_UPLOAD_ERROR if you don't want new code

                logAppInsightError("Invalid file type dropped", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "onDrop",
                    attemptedFileName: file?.name || null,
                    attemptedFileType: file?.type || null,
                    attemptedFileSize: file?.size || null
                }));

                setFileError("🚨 Only MISMO 3.4 XML files are allowed to create a loan.");
                showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
                setUploadedFile(null);
                setUploadedXmlContent("");
                return;
            }

            setFileError("");

            setUploadedFile(file);
            const reader = new FileReader();

            reader.onload = (e) => setUploadedXmlContent(e.target.result);

            reader.onerror = () => {
                const errorCode = logError("FILE_READ_ERROR"); // or FILE_UPLOAD_ERROR

                logAppInsightError("FileReader failed while reading XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "onDrop",
                    uploadedFileName: file?.name || null,
                    uploadedFileType: file?.type || null,
                    uploadedFileSize: file?.size || null,
                    rawError: reader?.error?.message || "FileReader error"
                }));

                setFileError("🚨 Failed to read the XML file. Please try again.");
                showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
                setUploadedFile(null);
                setUploadedXmlContent("");
            };

            reader.readAsText(file);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected: (fileRejections) => {
            const rej = fileRejections?.[0];
            const rejectedFile = rej?.file;

            const errorCode = logError("FILE_DROP_REJECTED");
            setFileError("🚨 Only MISMO 3.4 XML files are allowed to create a loan.");
            showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
            setUploadedFile(null);
            setUploadedXmlContent("");

            logAppInsightError("Drop rejected (onDropRejected)", buildUploadContext({
                problemId: String(errorCode),
                errorCode: String(errorCode),
                rejectedFileName: rejectedFile?.name || null,
                rejectedFileType: rejectedFile?.type || null,
                rejectedFileSize: rejectedFile?.size || null,
                rejectionErrors: (rej?.errors || []).map(e => ({
                    code: e?.code,
                    message: e?.message
                }))
            }));
        },
        accept: { "text/xml": [".xml"], "application/xml": [".xml"] }, // better than ".xml"
        multiple: false
    });

    const fetchExternalUserDetails = async (authCode, TPOUserId, state = "") => {
        setTpoUserId(TPOUserId);
        try {
            info("ExternalUserDetails loaded", { component: "LoanForm", userid: TPOUserId });
            setTpoCompanies([]);
            setTpoLoanOfficers([]);
            setTpoLoanProcessors([]);
            setTpoParent(null);
            setStateLicenses(null);
            setSelectedCompany("");
            setSelectedLoanOfficer("");
            setSelectedLoanProcessor("");

            const response = await getTPOContact(authCode, TPOUserId, state);
            if (response) {

                setTpoCompanies(response.companies || []);
                setTpoLoanOfficers(response.loanOfficers || []);
                setTpoLoanProcessors(response.loanProcessors || []);
                setTpoParent(response.parent || null);
                setStateLicenses(response.stateLicenses || null);

                if (response.companies && response.companies.length > 0) {
                    const company = response.companies[0];

                    setSelectedCompany(company.id.toString());

                    // Determine AE source
                    let aeList = [];

                    if (company.salesRepAe && company.salesRepAe.length > 0) {
                        // Prefer company AE
                        aeList = company.salesRepAe;
                    } else if (response.parent?.salesRepAe && response.parent.salesRepAe.length > 0) {
                        // Fallback to parent AE
                        aeList = response.parent.salesRepAe;
                    }

                    setSalesRepAeList(aeList);

                    // Auto-select if only one AE
                    if (aeList.length === 1) {
                        setSelectedAeUserId(aeList[0].userId);
                    }
                }
                setLoanOfficerList(
                    (response.loanOfficers || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(officer => ({
                            id: officer.id,
                            name: officer.name
                        }))
                );

                setLoanProcessorList(
                    (response.loanProcessors || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(processor => ({
                            id: processor.id,
                            name: processor.name
                        }))
                );

                // Cache the "main" org response (default behavior)
                setMainOrgCache(response);

                const mainCompany = (response.companies || [])[0];
                const children = mainCompany?.childOrganizations || [];

                const peer = (response.peerLoanAccess || response.PeerLoanAccess || "").trim();
                const mode = (response.accessMode || response.AccessMode || "").trim();

                setPeerLoanAccess(peer);

                const allowOrgSwitch = ENABLE_COMPANY_SWITCH && isReadWrite(peer) && isReadWrite(mode);

                const mainOrgEntityId = String(mainCompany?.orgId || "").trim(); // your "001"

                // Only show dropdown if children exist
                if (allowOrgSwitch && children.length > 0 && mainOrgEntityId) {
                    const TAB = "\u00A0\u00A0\u00A0\u00A0";
                    const options = [
                        {
                            value: mainOrgEntityId,
                            label: `${mainCompany.organizationName}`,   // parent line
                            isMain: true,
                            level: 0
                        },
                        ...children.map(ch => ({
                            value: String(ch.entityId),
                            label: `${TAB}↳ ${ch.entityName}`,
                            isMain: false,
                            level: 1
                        }))
                    ];

                    setOrgOptions(options);
                    setSelectedOrgEntityId(mainOrgEntityId); // default = parent
                } else {
                    setOrgOptions([]);
                    setSelectedOrgEntityId("");
                }
            }
        } catch (error) {
            console.error("Error fetching external user details:", error);
            const errorCode = logError("USER_FETCH_ERROR");
            setErrorMessage(`Error Code: ${errorCode} - fetching external user details.`);
            setShowErrorPopup(true);

            logAppInsightError(`${errorCode} - fetching external user details.`, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "LoanForm"
            });
        } finally {
            setSubmitting(false);
        }
    };

    const refreshContactsByOrg = async (orgEntityId) => {
        if (!orgEntityId) return;

        setSwitchingOrg(true);
        try {
            // reset selections when org changes
            setSelectedLoanOfficer("");
            setSelectedLoanProcessor("");

            const selectedOpt = orgOptions.find(o => o.value === orgEntityId);

            // MAIN company selected -> restore cached LO/LP
            if (selectedOpt?.isMain && mainOrgCache) {
                const response = mainOrgCache;

                setTpoCompanies(response.companies || []);

                // IMPORTANT: keep selectedCompany in sync (so submit uses correct company)
                const company = (response.companies || [])[0];
                if (company?.id != null) setSelectedCompany(String(company.id));

                setTpoLoanOfficers(response.loanOfficers || []);
                setTpoLoanProcessors(response.loanProcessors || []);
                setTpoParent(response.parent || null);
                setStateLicenses(response.stateLicenses || null);

                setLoanOfficerList(
                    (response.loanOfficers || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(officer => ({ id: officer.id, name: officer.name }))
                );

                setLoanProcessorList(
                    (response.loanProcessors || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(processor => ({ id: processor.id, name: processor.name }))
                );

                return;
            }

            // CHILD selected -> call new endpoint
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const resp = await getTPOContactByOrg(authCode, tpoUserId, propertyState, orgEntityId);

            setTpoCompanies(resp.companies || []);

            // IMPORTANT: keep selectedCompany in sync (so submit uses correct company)
            const company = (resp.companies || [])[0];
            if (company?.id != null) setSelectedCompany(String(company.id));

            setTpoLoanOfficers(resp.loanOfficers || []);
            setTpoLoanProcessors(resp.loanProcessors || []);
            setTpoParent(resp.parent || null);
            setStateLicenses(resp.stateLicenses || null);

            setLoanOfficerList(
                (resp.loanOfficers || [])
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                    .map(officer => ({ id: officer.id, name: officer.name }))
            );

            setLoanProcessorList(
                (resp.loanProcessors || [])
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                    .map(processor => ({ id: processor.id, name: processor.name }))
            );
        } catch (err) {
            const errorCode = logError("ORG_SWITCH_FAILED");

            logAppInsightError("Company / Branch switch failed", {
                problemId: errorCode,
                errorCode,
                component: "LoanForm",
                function: "refreshContactsByOrg",
                selectedOrgEntityId: orgEntityId,
                tpoUserId,
                propertyState,
                rawError: err?.message || String(err),
                responseData: err?.response?.data || null
            });

            console.error("Org switch failed:", err);

            showToast(
                `❌ Error Code: ${errorCode} - Failed to load contacts for selected company.`,
                8000
            );
        }
        finally {
            setSwitchingOrg(false);
        }
    };


    const handleSubmit = async () => {
        const selectedCompanyData = (tpoCompanies && tpoCompanies.length > 0)
            ? tpoCompanies[0]
            : null;
        const selectedLoanOfficerData = tpoLoanOfficers.find(lo => lo.id === selectedLoanOfficer);
        const selectedLoanProcessorData = tpoLoanProcessors.find(lp => lp.id === selectedLoanProcessor);

        const isCompanyRequired =
            ENABLE_COMPANY_SWITCH && orgOptions.length > 0;

        if (
            (isCompanyRequired && !selectedOrgEntityId) ||
            !selectedLoanOfficerData ||
            !selectedLoanProcessorData
        ) {
            const errorCode = logError("INVALID_CONTACT_SELECTION");

            logAppInsightError("Invalid contact selection during loan submit", {
                problemId: errorCode,
                errorCode,
                component: "LoanForm",
                function: "handleSubmit",
                peerLoanAccess,
                selectedOrgEntityId,
                selectedLoanOfficer,
                selectedLoanProcessor
            });

            setErrorMessage(
                `Error Code: ${errorCode} - Please select a valid ${isCompanyRequired ? "Company, " : ""
                }Loan Officer and Loan Processor.`
            );
            setShowErrorPopup(true);
            setLoading(false);
            return;
        }


        setSubmitting(true);

        let loDetails = null;
        let loLicense = null;
        let repOrgId = "";
        let repOrgName = "";

        try {
            loDetails = await getUserDetails(accessToken, selectedLoanOfficer);
            loLicense = getActiveLicenseForState(loDetails?.licenses, propertyState);

            const salesRepUserId = loDetails?.salesRep?.entityId;
            if (salesRepUserId) {
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();
                const org = await getSalesRepOrg(authCode, salesRepUserId);
                repOrgId = org?.orgId ? String(org.orgId).trim() : "";
                repOrgName = org?.orgName ? String(org.orgName).trim() : "";
            }

        } catch (e) {
            // AppInsights + stop submit
            const errorCode = logError("LO_ON_DEMAND_FETCH_ERROR");

            logAppInsightError("LO on-demand fetch failed", {
                problemId: errorCode,
                errorCode,
                component: "LoanForm",
                function: "handleSubmit",
                selectedLoanOfficer,
                propertyState,
                rawError: e?.message || String(e),
                responseData: e?.response?.data || null
            });

            setErrorMessage(`Error Code: ${errorCode} - Unable to validate Loan Officer (license/AE). Please try again.`);
            setShowErrorPopup(true);

            setSubmitting(false);
            setStep(4);
            return;
        }

        setStep(5);

        info("Submitting loan", { loanType });

        const channel = process.env.REACT_APP_LOAN_CHANNEL;

        // Always treat the SELECTED company (parent OR branch) as the "main" company for loan creation
        const effectiveCompany = selectedCompanyData; // this is tpoCompanies[0] (updated by refreshContactsByOrg)
        const parentCompany = tpoParent || null;


        // Base TPO object
        let tpo = {
            registerDate: new Date().toISOString(),
            importSource: "MISMO 3.4",
            underwritingDelegated: false,
            preferredAus: "NoPreference",
            sITEID: selectedCompanyData?.tpocSetup?.tpoSiteDetails?.[0]?.siteId ?? null,
            ...extractCompanyDetails(effectiveCompany),
            ...extractUserDetails(selectedLoanOfficerData, "lo", selectedCompanyData),
            ...extractUserDetails(selectedLoanProcessorData, "lp", selectedCompanyData)
        };

        // Add branch fields if parent exists
        if (parentCompany) {
            Object.assign(tpo, extractCompanyDetails(selectedCompanyData, "branch"));
        }

        const buildCommonLoanObject = () => {

            // Determine Parent Org NMLS ID
            const parentOrgNmlsId = tpoParent
                ? tpoParent?.businessInformation?.nmlsId || null
                : selectedCompanyData?.businessInformation?.nmlsId || null;

            let aeBranchId = "";
            let aeBranchName = "";

            if (Array.isArray(salesRepAeList) && salesRepAeList.length > 0) {
                const selectedAE = salesRepAeList.find(ae => ae.userId === selectedAeUserId);
                aeBranchId = selectedAE?.organizationId ? String(selectedAE.organizationId) : "";
                aeBranchName = selectedAE?.organizationName ? String(selectedAE.organizationName) : "";
            } else {
                aeBranchId = repOrgId ? String(repOrgId) : "";
                aeBranchName = repOrgName ? String(repOrgName) : "";
            }

            const common = {
                channel,
                tpo,
                customFields: [
                    {
                        fieldName: "CX.LOAN.TYPE",
                        value: loanType === "consumer" ? "Consumer" : "Business"
                    },
                    {
                        fieldName: "CX.PARENTORG.NMLS.ID",
                        value: parentOrgNmlsId
                    },
                    {
                        fieldName: "CX.AE.BRANCH.ID",
                        value: aeBranchId
                    },
                    {
                        fieldName: "CX.AE.BRANCH.NAME",
                        value: aeBranchName
                    }
                ],
                interviewersId: tpo.loId,
                interviewerEmail: selectedLoanOfficerData?.email ?? null,
                interviewerName: buildInterviewerName(selectedLoanOfficerData),
                originatorFirstName: selectedLoanOfficerData?.firstName ?? null,
                originatorLastName: selectedLoanOfficerData?.lastName ?? null,
                nmlsLoanOriginatorId: selectedLoanOfficerData?.nmlsId ?? null,
                originatorMiddleName: selectedLoanOfficerData?.middleName ?? null,
                originatorSuffixName: selectedLoanOfficerData?.suffix ?? null,
                interviewerPhoneNumber: selectedLoanOfficerData?.phone ?? null,
                loanSource: "Encompass TPO Connect",
                mortgageType: "Conventional"
            };

            // Business Purpose: set Application Date (loan.originationDate) = Today
            if ((loanType || "").trim().toLowerCase() === "business") {
                common.originationDate = getPhoenixISODate();
            }

            return common;
        };

        const runSoftLicenseWarnings = ({
            loanType,
            stateLicenses,
            selectedCompanyData,
            selectedLoanOfficerData,
            propertyState,
            propertyCity,
            loLicense,
            showToast
        }) => {
            try {
                const warnings = collectSoftLicenseWarnings(
                    loanType,
                    stateLicenses,
                    selectedCompanyData,
                    selectedLoanOfficerData,
                    propertyState,
                    propertyCity,
                    loLicense
                );

                if (!warnings || warnings.length === 0) return;

                // Normalize to array
                const rules = Array.isArray(stateLicenses)
                    ? stateLicenses
                    : (stateLicenses ? [stateLicenses] : []);

                const lt = (loanType || '').trim().toLowerCase();
                const effState = (propertyState || '').trim().toUpperCase();

                // Find rules for this state only (city ignored)
                const stateRules = rules.filter(r =>
                    ((r.stateCode || '').trim().toUpperCase() === effState)
                );

                const matchingRule = stateRules.find(r => {
                    const ruleLoanType = (r.loanType || '').trim().toLowerCase();
                    return ruleLoanType === lt || ruleLoanType === '';
                });

                const brokerEmpty =
                    matchingRule && (matchingRule.brokerLicenseRequired || '').trim() === '';
                const loEmpty =
                    matchingRule && (matchingRule.loanOriginatorLicenseRequired || '').trim() === '';

                if (matchingRule && brokerEmpty && loEmpty) {
                    const isBusiness = lt === 'business';

                    const msg =
                        `This ${isBusiness ? 'Business Purpose' : 'Consumer'} loan is not eligible for ` +
                        `${isBusiness ? 'investment' : 'Consumer'} transactions in ${propertyCity}, ${propertyState}. ` +
                        `You can create the loan, but you cannot submit it for this location.`;

                    showToast(msg, 8000);
                } else {
                    const html = `License warnings:<br>- ${warnings.join('<br>- ')}`;
                    showToast(html, 8000);
                }
            } catch (err) {
                console.error("Soft licensing check failed:", err);
            }
        };

        const submitLoan = async (loanObject) => {
            try {
                const createdLoan = await createLoan(accessToken, loanObject, loanType, tpoUserId);
                if (createdLoan) {
                    await updateLienHolderFields(accessToken, createdLoan.id);

                    // URLA.X81 is a lock field - createLoan does not set it, so write it (locked) here.
                    // The loan already exists, so a failure is logged rather than failing the create.
                    if (method === "import") {
                        const rentalIncomeWrite = getImportedRentalIncomeFieldWrite(loanObject);
                        if (rentalIncomeWrite.length > 0) {
                            try {
                                await fieldWriter(accessToken, createdLoan.id, rentalIncomeWrite);
                            } catch (err) {
                                logAppInsightError("URLA.X81 field write failed after loan create", {
                                    component: "LoanForm",
                                    function: "submitLoan",
                                    loanId: createdLoan.id,
                                    rawError: err?.message
                                });
                            }
                        }
                    }

                    const selectedAE = salesRepAeList.find(ae => ae.userId === selectedAeUserId);

                    await applyAEtoLoan(accessToken, createdLoan.id, selectedAE);

                    redirectToLoanPage(createdLoan.id);
                }
            } catch (error) {
                setStep(4);
                handleLoanError(error);
            } finally {
                setSubmitting(false);
            }
        };

        if (method === "import") {
            if (!uploadedXmlContent) {
                const errorCode = logError("FILE_UPLOAD_ERROR");
                setErrorMessage(`Error Code: ${errorCode} - Please upload a MISMO XML file first.`);
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            try {
                const loanObject = await convertMismoToLoanObject(accessToken, uploadedXmlContent);
                if (loanObject) {
                    Object.assign(loanObject, buildCommonLoanObject());

                    // Update companyId in BROKER_LENDER contact if it exists
                    const brokerLenderContact = loanObject.contacts?.find(c => c.contactType === "BROKER_LENDER");

                    if (brokerLenderContact && selectedCompanyData?.businessInformation?.nmlsId) {
                        brokerLenderContact.companyId = selectedCompanyData.businessInformation.nmlsId;
                    }

                    if (loanObject.loanAmortizationType === "AdjustableRate") {
                        loanObject.loanProductData = loanObject.loanProductData ?? {};
                        const armIndexType = "SOFR_30DayAvg";
                        loanObject.loanProductData.armDisclosureType = armIndexType;
                        try {
                            const indexValue = await getArmIndex(accessToken, armIndexType);
                            if (indexValue != null) {
                                loanObject.loanProductData.indexCurrentValuePercent = indexValue;
                                loanObject.loanProductData.indexDate = getPhoenixISODate();
                            }
                        } catch (err) {
                            console.error("ARM index lookup failed:", err);
                        }
                    }

                    // Set company state license if available
                    const companyLicense = selectedCompanyData?.license;
                    if (companyLicense?.licenseNumber) {
                        loanObject.interviewersCompanyStateLicense = companyLicense.licenseNumber;
                    }

                    // Set loan officer license if available
                    const officerLicense = loLicense;
                    if (officerLicense?.licenseNumber) {
                        loanObject.interviewerLicenseIdentifier = officerLicense.licenseNumber;
                    } else {
                        loanObject.interviewerLicenseIdentifier = "";
                    }

                    if (loanType === "business" && Array.isArray(loanObject.applications) && loanObject.applications.length > 0) {
                        loanObject.applications[0].propertyUsageType = "Investor";
                    }

                    runSoftLicenseWarnings({
                        loanType,
                        stateLicenses,
                        selectedCompanyData,
                        selectedLoanOfficerData,
                        propertyState,
                        propertyCity,
                        loLicense,
                        showToast
                    });

                    // SkyTPO: Estimated Net Monthly Rental Income (URLA.X81) is only kept when the file has REO properties
                    clearRentalIncomeWithoutReo(loanObject);

                    addLoCompensationIfAvailable(loanObject, selectedCompanyData, selectedLoanOfficer);
                    await submitLoan(loanObject);
                }
            } catch (error) {
                setStep(4);
                logAppInsightError("MISMO convert failed (handleSubmit)", {
                    problemId: logError("MISMO_CONVERT_ERROR"),
                    errorCode: "MISMO_CONVERT_ERROR",
                    component: "LoanForm",
                    function: "handleSubmit",
                    step,
                    method,
                    loanType,
                    rawError: error?.message,
                    responseData: error?.response?.data || null,
                    hasUploadedFile: !!uploadedFile,
                    uploadedFileName: uploadedFile?.name || null,
                    hasXmlContent: !!uploadedXmlContent
                });

                handleLoanError(error);
                setSubmitting(false);
            }
        } else if (method === "manual") {
            const loanObject = buildCommonLoanObject();

            applyStaticImportParityForManual(loanObject, {
                loanType,
                selectedCompanyData,
                loLicense
            });

            addLoCompensationIfAvailable(loanObject, selectedCompanyData, selectedLoanOfficer);
            await submitLoan(loanObject);
        }
    };

    const applyAEtoLoan = async (accessToken, loanId, selectedAE) => {
        if (!selectedAE) return;

        const roles = await getMilestoneFreeRoles(accessToken, loanId);

        const aeRole = roles.find(r =>
            r?.loanAssociate?.role?.entityName === "Account Executive"
        );

        if (!aeRole) {
            console.warn("No Account Executive role found for this loan.");
            return;
        }

        const payload = [
            {
                id: aeRole.id,
                loanAssociate: {
                    loanAssociateType: "User",
                    user: {
                        entityId: selectedAE.userId,
                        entityType: "User"
                    },
                    email: selectedAE.email || null,
                    phone: selectedAE.phone || null,
                    cellPhone: selectedAE.cellPhone || null,
                    fax: selectedAE.fax || null,
                    writeAccess: false
                }
            }
        ];

        await patchMilestoneFreeRoles(accessToken, loanId, payload);
    };

    const redirectToLoanPage = async (loanId) => {
        const pageName = "Loan Summary";

        if (window.elli && window.elli.script) {
            window.elli.script.getObject("tpoApplication").then(async (tpoAppObject) => {
                const section = pageName.startsWith("URLA:") ? pageName.split(":")[1].trim() : null;
                await tpoAppObject.pageRedirect(section ? "urla" : pageName, { id: loanId, section });
            });
        } else {
            console.warn("elli.script is not available for redirection.");
        }
    };

    const handleLoanError = (error) => {
        const errorCode = logError("LOAN_PROCESS_ERROR");
        const apiData = error?.response?.data;

        const { message, parsedErrors } = formatLoanErrorMessage({
            errorCode,
            apiData,
            error
        });

        setErrorMessage(message);
        setShowErrorPopup(true);

        logAppInsightError("Loan processing failed", {
            problemId: errorCode,
            errorCode,
            errorMessage: message,
            parsedErrors,
            component: "LoanForm"
        });
    };

    const addLoCompensationIfAvailable = (loanObject, selectedCompanyData, selectedLoanOfficer) => {

        if (selectedCompanyData?.loComp?.loCompHistory?.length > 0) {
            const paidCompensation = process.env.REACT_APP_COMPENSATION_PAID;

            const loComp = selectedCompanyData.loComp.loCompHistory[0];

            const { lastAppliedDate, lastAppliedDateTime } = getCurrentPSTDateAndTimeISO();

            loanObject.loCompensation = {
                basePlanAdditonalAmountForBroker: loComp.amount || 0,
                basePlanAmountForBroker: loComp.amount || 0,
                basePlanMaximumAmountForBroker: loComp.maxAmount || 0,
                basePlanMinimumAmountForBroker: loComp.minAmount || 0,
                basePlanRateForBroker: loComp.percentAmt || 0,
                lastAppliedDate,
                lastAppliedDateTime,
                loanAmountType: loComp.percentAmtIsOf || "Base Loan",
                loanOriginatorId: selectedCompanyData.id || null,
                netAdjustedAmountForBroker: loComp.amount || 0,
                planDate: loComp.startDate ? loComp.startDate.split('T')[0] : null,
                planName: loComp.name || null,
                roundingMethod: loComp.rounding || null,
                triggerField: loComp.triggerBasis || null,
                whoPaidCompensation: paidCompensation,
                loanOriginatorName: selectedCompanyData.companyLegalName,
                whoPaidCompensation: "Borrower Paid"
            };
        }
        else {
            loanObject.loCompensation = {
                whoPaidCompensation: "Borrower Paid"
            };
        }

    };

    const LoanTypeBadge = () => {
        if (!loanType) return null;

        const handleClick = () => {
            // If forced business flow, never go to step 1
            if (forceBusinessFlow) {
                setStep(2);
                return;
            }
            // Move to Step 1 unconditionally
            setStep(1);

            // Reset fields to initial state
            setUploadedFile(null);
            setUploadedXmlContent("");
            setFileError("");
        };

        return (
            <div
                className="loan-type-badge"
                onClick={handleClick}
                style={{ top: '1rem', marginBottom: '2rem' }}
            >
                <span>{loanType === "consumer" ? "🧍 Consumer Loan" : "🏢 Business Loan"}</span>
            </div>
        );
    };

    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastAnimation, setToastAnimation] = useState('animate-fade-in-right');

    const showToast = (htmlMessage, timeoutMs = 5000) => {
        setToastMessage(htmlMessage);
        setToastVisible(true);
        setToastAnimation('animate-fade-in-right');
        // auto-hide
        setTimeout(() => {
            setToastAnimation('animate-fade-out-right');
            setTimeout(() => setToastVisible(false), 500);
        }, timeoutMs);
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            {submitting && <FormLoader />}
            {switchingOrg && <FormLoader />}
            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={() => setToastVisible(false)}
                animationClass={toastAnimation}
            />
            <div className="loan-container relative">

                {/* Step 1 */}
                {step === 1 && !hideStep1 && (
                    <>
                        <h2 className="loan-heading">What is the purpose of this loan?</h2>
                        <p className="loan-subheading">Help us understand whether it's for business or personal use.</p>

                        <div className="loan-options">
                            <button
                                className={`loan-button consumer ${loanType === "consumer" ? "selected" : ""}`}
                                onClick={() => {
                                    setLoanType("consumer");
                                    setStep(2); // Immediately go to next step
                                }}
                            >
                                <div className="flex flex-col items-center">
                                    🧍 Consumer
                                    <span className="block text-xs">(OO/2nd Home)</span>
                                </div>
                            </button>
                            <button
                                className={`loan-button business ${loanType === "business" ? "selected" : ""}`}
                                onClick={() => {
                                    setLoanType("business");
                                    setStep(2); // Immediately go to next step
                                }}
                            >
                                <div className="flex flex-col items-center">
                                    🏢 Business
                                    <span className="block text-xs">(NOO/Investment)</span>
                                </div>
                            </button>
                        </div>

                    </>
                )}

                {/* STEP 2: Method */}
                {step === 2 && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">How would you like to enter loan data?</h2>

                        <p className="loan-subheading">You can either import a file or fill it out manually.</p>

                        <div className="loan-options">
                            <button
                                className={`loan-button import ${method === "import" ? "selected" : ""}`}
                                onClick={() => {
                                    setMethod("import");
                                    setStep(3);
                                }}
                            >
                                📁 Import File
                            </button>
                            <button
                                className={`loan-button manual ${method === "manual" ? "selected" : ""}`}
                                onClick={() => {
                                    setMethod("manual");
                                    handleNextManual();
                                }}
                            >
                                📝 Manual Entry
                            </button>
                        </div>

                        {!hideBackInStep2 && (
                            <button className="back-button" onClick={() => setStep(1)}>← Back</button>
                        )}
                    </>
                )}


                {/* STEP 3: File Upload */}
                {step === 3 && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">Upload Your Loan File</h2>
                        <p className="loan-subheading">Accepted formats: .xml</p>

                        <div {...getRootProps()} className="upload-box">
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <p className="text-blue-600">Drop the file here...</p>
                            ) : (
                                <p className="text-gray-600"><span className="upload-icon">☁️</span> Drag & drop or click to browse</p>
                            )}
                            {uploadedFile && <p className="text-green-600 text-sm mt-2">Uploaded: {uploadedFile.name}</p>}
                            {fileError && <p className="text-red-600 text-sm mt-2">{fileError}</p>}
                        </div>

                        <p className="text-xs text-red-500 mt-2">Only MISMO 3.4-compliant XML files are supported for loan creation.</p>

                        <div className="flex justify-between mt-8">
                            <button className="back-button" onClick={() => setStep(2)}>← Back</button>
                            <button className="submit-button" disabled={!uploadedFile} onClick={handleNext}>Next →</button>
                        </div>

                    </>
                )}

                {/* Step 4 */}
                {(step === 4 || step === 5) && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">Select Contacts</h2>
                        <div className="mt-6">
                            {ENABLE_COMPANY_SWITCH && orgOptions.length > 0 && (
                                <div className="mb-5">
                                    <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>
                                        Company
                                    </h3>

                                    <select
                                        value={selectedOrgEntityId}
                                        onChange={async (e) => {
                                            const val = e.target.value;
                                            setSelectedOrgEntityId(val);
                                            await refreshContactsByOrg(val); // parent restores cached; child calls API
                                        }}
                                        className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                    >
                                        {orgOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {salesRepAeList.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>Account Executive</h3>
                                    <div className="space-y-6">
                                        <div>
                                            <select
                                                value={selectedAeUserId}
                                                onChange={(e) => setSelectedAeUserId(e.target.value)}
                                                className="custom-select w-full border border-gray-300 rounded-md p-2 mb-5 text-xs"
                                            >
                                                <option value="">Select</option>
                                                {salesRepAeList.map((ae) => (
                                                    <option key={ae.userId} value={ae.userId}>
                                                        {ae.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>Loan Officer</h3>

                            <div className="space-y-6">
                                <div>
                                    <select
                                        value={selectedLoanOfficer}
                                        onChange={(e) => setSelectedLoanOfficer(e.target.value)}
                                        className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                    >
                                        <option value="">Select</option>
                                        {loanOfficerList.map((officer) => (
                                            <option key={officer.id} value={officer.id}>
                                                {officer.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <h3 className="text-lg font-semibold text-blue-900 mb-2 mt-5" style={{ color: 'var(--blue-accent)' }}>Loan Processor</h3>
                            <div className="space-y-6">
                                <div>
                                    <select
                                        value={selectedLoanProcessor}
                                        onChange={(e) => setSelectedLoanProcessor(e.target.value)}
                                        className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                    >
                                        <option value="">Select</option>
                                        {loanProcessorList.map((processor) => (
                                            <option key={processor.id} value={processor.id}>
                                                {processor.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-between mt-8">
                                <button className="back-button" onClick={() => setStep(method === "import" ? 3 : 2)}>← Back</button>
                                <button
                                    className="submit-button"
                                    disabled={
                                        !selectedLoanOfficer ||
                                        !selectedLoanProcessor ||
                                        (salesRepAeList.length > 0 && !selectedAeUserId)
                                    }
                                    onClick={handleSubmit}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>

                    </>
                )}

                <div className="progress-container mt-5">
                    <div className="progress-bar" style={{ width: `${getProgressWidth(step)}%` }} />
                </div>

                {showErrorPopup && (() => {
                    const parsed = parseErrorModalBlocks(errorMessage);
                    if (!parsed) return null;

                    const { heading, general, remaining } = parsed;

                    return (
                        <div className="app-modal-overlay">
                            <div className="app-modal" role="dialog" aria-modal="true" aria-labelledby="app-modal-title">
                                <div className="app-modal-header">
                                    <div id="app-modal-title" className="app-modal-title">Error</div>
                                    <button
                                        className="app-modal-x"
                                        aria-label="Close"
                                        onClick={() => setShowErrorPopup(false)}
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="app-modal-body">
                                    {/* Heading always its own block */}
                                    <div className="app-modal-heading" style={{ textAlign: "left", fontWeight: 500 }}>
                                        {heading}
                                    </div>

                                    {/* General details on its own line */}
                                    {general ? (
                                        <div className="app-modal-general" style={{ textAlign: "left", marginTop: "8px" }}>
                                            {general}
                                        </div>
                                    ) : null}

                                    {/* Detailed error blocks */}
                                    {remaining.length > 0 && (
                                        <ErrorBlocks remaining={remaining} />
                                    )}
                                </div>

                                <div className="app-modal-footer">
                                    <button
                                        className="submit-button"
                                        onClick={() => setShowErrorPopup(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

            </div>
        </>
    );
};

export default LoanForm;